import DOMPurify from "dompurify";

const ALLOWED_STYLE_PROPERTIES = new Set([
  "clear",
  "color",
  "font-weight",
  "list-style-type",
  "margin",
  "margin-bottom",
  "margin-left",
  "margin-right",
  "margin-top",
  "padding",
  "padding-bottom",
  "padding-left",
  "padding-right",
  "padding-top",
  "text-align",
  "text-decoration",
]);

const BLOCKED_STYLE_VALUE_PATTERN = /(url\s*\(|expression\s*\(|javascript:|vbscript:|data:)/i;
const ALLOWED_STYLE_VALUE_PATTERN = /^[#(),.%\w\s-]+$/;
const BLOCKED_URL_PATTERN = /^(?:\s*javascript:|\s*vbscript:|\s*data:)/i;

const toKebabCase = (value: string) =>
  value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

const cleanStyleValue = (value: string) => {
  const trimmedValue = value.trim().replace(/^['"]|['"]$/g, "");

  if (
    !trimmedValue ||
    BLOCKED_STYLE_VALUE_PATTERN.test(trimmedValue) ||
    !ALLOWED_STYLE_VALUE_PATTERN.test(trimmedValue)
  ) {
    return "";
  }

  return trimmedValue;
};

const sanitizeInlineStyle = (styleValue: string) => {
  const styleDeclarations = styleValue
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean);

  const sanitizedDeclarations = styleDeclarations
    .map((declaration) => {
      const [rawProperty, ...rawValueParts] = declaration.split(":");
      if (!rawProperty || rawValueParts.length === 0) {
        return "";
      }

      const property = toKebabCase(rawProperty.trim()).toLowerCase();
      if (!ALLOWED_STYLE_PROPERTIES.has(property)) {
        return "";
      }

      const cleanedValue = cleanStyleValue(rawValueParts.join(":"));
      if (!cleanedValue) {
        return "";
      }

      return `${property}: ${cleanedValue}`;
    })
    .filter(Boolean);

  return sanitizedDeclarations.join("; ");
};

const normalizeStyleObject = (styleObject: string) => {
  const declarations = styleObject
    .split(",")
    .map((declaration) => declaration.trim())
    .filter(Boolean);

  const normalizedDeclarations = declarations
    .map((declaration) => {
      const [rawProperty, ...rawValueParts] = declaration.split(":");
      if (!rawProperty || rawValueParts.length === 0) {
        return "";
      }

      const property = toKebabCase(rawProperty.trim());
      const value = cleanStyleValue(rawValueParts.join(":"));
      if (!value) {
        return "";
      }

      return `${property}: ${value}`;
    })
    .filter(Boolean);

  return normalizedDeclarations.join("; ");
};

const normalizeJsxLikeAttributes = (html: string) => {
  let normalizedHtml = html;

  normalizedHtml = normalizedHtml.replace(
    /style=\{\{([\s\S]*?)\}\}/g,
    (_match, styleObject: string) => {
      const normalizedStyle = normalizeStyleObject(styleObject);
      return normalizedStyle ? `style="${normalizedStyle}"` : "";
    }
  );

  normalizedHtml = normalizedHtml.replace(
    /(\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*)=\{([^{}]+)\}/g,
    (_match, attributeName: string, rawValue: string) => {
      const cleanedValue = rawValue.trim().replace(/^['"]|['"]$/g, "");

      if (!cleanedValue) {
        return "";
      }

      return `${attributeName}="${cleanedValue}"`;
    }
  );

  return normalizedHtml;
};

export const sanitizeTermsHtml = (html: string): string => {
  if (!html) {
    return "";
  }

  const normalizedHtml = normalizeJsxLikeAttributes(html);

  const sanitizedHtml = DOMPurify.sanitize(normalizedHtml, {
    ALLOWED_TAGS: [
      "a",
      "b",
      "blockquote",
      "br",
      "div",
      "em",
      "i",
      "li",
      "ol",
      "p",
      "span",
      "strong",
      "table",
      "tbody",
      "td",
      "th",
      "thead",
      "tr",
      "u",
      "ul",
    ],
    ALLOWED_ATTR: [
      "clear",
      "colspan",
      "href",
      "rel",
      "rowspan",
      "start",
      "style",
      "target",
      "type",
    ],
    FORBID_TAGS: [
      "button",
      "embed",
      "form",
      "iframe",
      "input",
      "meta",
      "object",
      "script",
      "select",
      "style",
      "textarea",
    ],
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
  });

  const parser = new DOMParser();
  const document = parser.parseFromString(
    `<div>${sanitizedHtml}</div>`,
    "text/html"
  );
  const rootElement = document.body.firstElementChild as HTMLElement | null;

  if (!rootElement) {
    return "";
  }

  rootElement.querySelectorAll<HTMLElement>("*").forEach((element) => {
    element.getAttributeNames().forEach((attributeName) => {
      if (attributeName.toLowerCase().startsWith("on")) {
        element.removeAttribute(attributeName);
      }
    });

    if (element.hasAttribute("style")) {
      const sanitizedStyle = sanitizeInlineStyle(
        element.getAttribute("style") || ""
      );

      if (sanitizedStyle) {
        element.setAttribute("style", sanitizedStyle);
      } else {
        element.removeAttribute("style");
      }
    }

    if (element.tagName.toLowerCase() === "a") {
      const href = element.getAttribute("href");
      if (href && BLOCKED_URL_PATTERN.test(href)) {
        element.removeAttribute("href");
      }

      if (element.getAttribute("target") === "_blank") {
        element.setAttribute("rel", "noopener noreferrer");
      }
    }
  });

  return rootElement.innerHTML;
};
